import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./FormationCreate.module.sass";

// Screens
import FormationInformations from "./FormationInformations";
import ResponsableInformations from "./ResponsableInformations";
import AddressInformations from "./AddressInformations";
import CentreInformations from "./CentreInformations";
import CostsInformations from "./CostsInformations";
import ConventionInformations from "./ConventionInformations";

// Services
import { updateFormation } from "../../../services/formation.service";
import { showSuccess, showError } from "../../../services/toast.service";

const NameAndDescription = ({ formation: initialFormation }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formationData, setFormationData] = useState({});
  const [selectedCenter, setSelectedCenter] = useState(null);

  // Initialiser les données de la formation lorsque les props changent
  useEffect(() => {
    if (initialFormation) {
      setFormationData(initialFormation);

      // Si un centre est associé à la formation, on le sélectionne
      if (initialFormation.center_id) {
        setSelectedCenter({
          id: initialFormation.center_id
        });
      }
    }
  }, [initialFormation]);

  const handleFormationChange = (e) => {
    const { name, value } = e.target;
    console.log(`handleFormationChange: ${name} = ${value}`);
    setFormationData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormationData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name, value) => {
    setFormationData((prev) => ({ ...prev, [name]: value }));

    // Si on active le switch place_Identical_training et qu'un centre est sélectionné
    // on copie les données d'adresse du centre
    if (name === 'place_Identical_training' && value === true && selectedCenter) {
      setFormationData((prev) => ({
        ...prev,
        address: selectedCenter.address || '',
        address_complement: selectedCenter.address_complement || '',
        zip_code: selectedCenter.zip_code || '',
        city: selectedCenter.city || '',
        department: selectedCenter.department || '',
        country: selectedCenter.country || 'France'
      }));
    }
  };

  const handleSelectCenter = (center) => {
    setSelectedCenter(center);
    setFormationData((prev) => ({
      ...prev,
      center_id: center.id,
      // Si l'adresse est identique, on copie les données du centre
      ...(formationData.place_Identical_training && center.addressData ? {
        address: center.addressData.address || '',
        address_complement: center.addressData.address_complement || '',
        zip_code: center.addressData.zip_code || '',
        city: center.addressData.city || '',
        department: center.addressData.department || '',
        country: center.addressData.country || 'France'
      } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Conversion des valeurs numériques
      const formationToSave = {
        ...formationData,
        course_duration: formationData.course_duration ? parseInt(formationData.course_duration) : null,
        number_of_hoursinstudy: formationData.number_of_hoursinstudy ? parseInt(formationData.number_of_hoursinstudy) : null,
        cost_diplome: formationData.cost_diplome ? parseFloat(formationData.cost_diplome) : null,
        numberOfAccommodations: formationData.numberOfAccommodations ? parseInt(formationData.numberOfAccommodations) : null,
        numberOfMeals: formationData.numberOfMeals ? parseInt(formationData.numberOfMeals) : null,
        // S'assurer que les boolean sont bien des boolean
        fraisHebergement: Boolean(formationData.fraisHebergement),
        fraisRestauration: Boolean(formationData.fraisRestauration),
        premierEquipement: Boolean(formationData.premierEquipement),
        fraisMobilite: Boolean(formationData.fraisMobilite),
        // S'assurer que contract_types est un tableau, même s'il est vide
        contract_types: Array.isArray(formationData.contract_types) ? formationData.contract_types : [],
        // Inclure le fichier de convention s'il existe
        convention_file: formationData.convention_file || null
      };

      // Log pour débogage
      console.log('FormationData avant sauvegarde:', formationData);
      console.log('FormationToSave:', formationToSave);
      
      // Log spécifique pour le fichier de convention
      if (formationData.convention_file) {
        console.log('Fichier de convention détecté:', formationData.convention_file.name);
      }
      
      // Si l'adresse est identique au centre, on utilise les données du centre
      if (formationToSave.place_Identical_training && selectedCenter) {
        formationToSave.address = selectedCenter.address || '';
        formationToSave.address_complement = selectedCenter.address_complement || '';
        formationToSave.zip_code = selectedCenter.zip_code || '';
        formationToSave.city = selectedCenter.city || '';
        formationToSave.department = selectedCenter.department || '';
        formationToSave.country = selectedCenter.country || 'France';
      }

      await updateFormation(formationData.id, formationToSave);
      showSuccess("Formation mise à jour avec succès!");
    } catch (err) {
      const errorMessage =
        "Impossible de mettre à jour la formation: " +
        (err.message || "Veuillez vérifier les informations saisies.");
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.container}>

        <div className={styles.formContainer}>
          {error && <div className={styles.error}>{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className={styles.row}>
              <div className={styles.col}>
                <FormationInformations
                  formation={formationData}
                  onFormationChange={handleFormationChange}
                  onDropdownChange={handleDropdownChange}
                  loading={loading}
                />
                <CostsInformations
                  formation={formationData}
                  onFormationChange={handleFormationChange}
                  loading={loading}
                />
                <ResponsableInformations
                  formation={formationData}
                  onFormationChange={handleFormationChange}
                  loading={loading}
                />
                <CentreInformations
                  formation={formationData}
                  onSelectCenter={handleSelectCenter}
                  onSwitchChange={handleSwitchChange}
                  onSubmit={handleSubmit}
                  loading={loading}
                />
                <AddressInformations
                  formation={formationData}
                  onFormationChange={handleFormationChange}
                  loading={loading}
                  visible={!formationData.place_Identical_training}
                  onSubmit={handleSubmit}
                />
                <ConventionInformations
                  formation={formationData}
                  onFormationChange={handleFormationChange}
                  onDropdownChange={handleDropdownChange}
                  loading={loading}
                  onSubmit={handleSubmit}
                />
              </div>
            </div>
            {/* Le bouton de soumission est maintenant dans le composant ConventionInformations */}
          </form>
        </div>
      </div>
    </>
  );
};

export default NameAndDescription;
